/**
 * Small checks that turn untyped JSON from the server into typed values. A response that
 * breaks the API contract raises `JsonContractError`, which the HTTP client reports as an
 * unexpected response. Nothing is cast unchecked.
 */
export class JsonContractError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "JsonContractError";
  }
}

export type JsonObject = Readonly<Record<string, unknown>>;

export function readObject(value: unknown, context: string): JsonObject {
  if (!isJsonObject(value)) {
    throw new JsonContractError(`Expected ${context} to be a JSON object.`);
  }
  return value;
}

export function isJsonObject(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function readString(object: JsonObject, member: string): string {
  const value = object[member];
  if (typeof value !== "string") {
    throw new JsonContractError(`Expected "${member}" to be a string.`);
  }
  return value;
}

export function readOptionalString(object: JsonObject, member: string): string | undefined {
  const value = object[member];
  if (value === undefined || value === null) {
    return undefined;
  }
  if (typeof value !== "string") {
    throw new JsonContractError(`Expected "${member}" to be a string when present.`);
  }
  return value;
}

export function readLiteral<const T extends string>(
  object: JsonObject,
  member: string,
  expected: T,
): T {
  const value = readString(object, member);
  if (value !== expected) {
    throw new JsonContractError(`Expected "${member}" to be "${expected}".`);
  }
  return expected;
}

/** A string, or `null` when the member is `null`. A missing member breaks the contract. */
export function readNullableString(object: JsonObject, member: string): string | null {
  const value = object[member];
  if (value === null) {
    return null;
  }
  if (typeof value !== "string") {
    throw new JsonContractError(`Expected "${member}" to be a string or null.`);
  }
  return value;
}

export function readInteger(object: JsonObject, member: string): number {
  const value = object[member];
  if (typeof value !== "number" || !Number.isInteger(value)) {
    throw new JsonContractError(`Expected "${member}" to be an integer.`);
  }
  return value;
}

/** An array of integers, such as entry numbers. */
export function readIntegers(object: JsonObject, member: string): number[] {
  return readArray(object[member], `"${member}"`, (item) => readInteger({ [member]: item }, member));
}

export function readNullableInteger(object: JsonObject, member: string): number | null {
  return object[member] === null ? null : readInteger(object, member);
}

export function readBoolean(object: JsonObject, member: string): boolean {
  const value = object[member];
  if (typeof value !== "boolean") {
    throw new JsonContractError(`Expected "${member}" to be a boolean.`);
  }
  return value;
}

/** One of a closed set of strings, such as an OpenAPI enum. */
export function readOneOf<const T extends string>(
  object: JsonObject,
  member: string,
  allowed: readonly T[],
): T {
  const value = readString(object, member);
  const match = allowed.find((candidate) => candidate === value);
  if (match === undefined) {
    throw new JsonContractError(`Expected "${member}" to be one of ${allowed.join(", ")}.`);
  }
  return match;
}

/** An array, each item checked by `readItem`. */
export function readArray<T>(value: unknown, context: string, readItem: (item: unknown) => T): T[] {
  if (!Array.isArray(value)) {
    throw new JsonContractError(`Expected ${context} to be a JSON array.`);
  }
  return value.map((item: unknown) => readItem(item));
}

/** A JSON object whose values are all strings, such as document metadata. */
export function readStringRecord(object: JsonObject, member: string): Readonly<Record<string, string>> {
  const record = readObject(object[member], `"${member}"`);
  const entries = Object.entries(record).map(([key, value]) => {
    if (typeof value !== "string") {
      throw new JsonContractError(`Expected every value of "${member}" to be a string.`);
    }
    return [key, value] as const;
  });
  const decoded = Object.fromEntries(entries);
  Object.setPrototypeOf(decoded, null);
  return decoded;
}

/** A JSON object whose values are all whole numbers, such as counts by kind. */
export function readIntegerRecord(object: JsonObject, member: string): Readonly<Record<string, number>> {
  const record = readObject(object[member], `"${member}"`);
  const entries = Object.entries(record).map(([key, value]) => {
    if (typeof value !== "number" || !Number.isInteger(value)) {
      throw new JsonContractError(`Expected every value of "${member}" to be a whole number.`);
    }
    return [key, value] as const;
  });
  const decoded = Object.fromEntries(entries);
  Object.setPrototypeOf(decoded, null);
  return decoded;
}
